import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../environments/environment';

// DTOs matching your NestJS backend
export interface CreateVehicleDto {
  CustUserId: string;
  Vin?: string;
  Year?: string;
  Make?: string;
  Model?: string;
  LicensePlate?: string;
  PlateState?: string;
}

export interface UpdateVehicleDto {
  Vin?: string;
  Year?: string;
  Make?: string;
  Model?: string;
  LicensePlate?: string;
  PlateState?: string;
}

export interface VehicleResponseDto {
  VehicleId: string;
  CustUserId: string;
  Vin?: string;
  Year?: string;
  Make?: string;
  Model?: string;
  LicensePlate?: string;
  PlateState?: string;
  CreatedAt: Date;
  UpdatedAt: Date;
}

export interface VehicleImageResponseDto {
  ImageId: string;
  VehicleId: string;
  ImageUrl: string;
  IsPrimary: boolean;
  CreatedAt: Date;
  UpdatedAt: Date;
}

export interface UpdateVehicleImageDto {
  IsPrimary: boolean;
}

export interface VehicleWithImagesResponseDto extends VehicleResponseDto {
  VehicleImages: VehicleImageResponseDto[];
  CustEmail: string;
  CustFirstName: string;
  CustLastName: string;
}

/** Why a destination vehicle looks like the same physical car. */
export type VehicleMatchReason = 'VIN' | 'PLATE' | 'YEAR_MAKE_MODEL';

export interface VehicleMergeCandidateDto {
  VehicleId: string;
  Vin?: string;
  Year?: string;
  Make?: string;
  Model?: string;
  LicensePlate?: string;
  PlateState?: string;
  MatchReason: VehicleMatchReason;
  InvoiceCount: number;
  ImageCount: number;
}

export interface TransferVehicleDto {
  NewCustUserId: string;
  // Omit to keep the vehicle as a separate record in the new garage.
  MergeIntoVehicleId?: string;
}

export interface TransferVehicleResponseDto {
  // The surviving vehicle — differs from the transferred one when merged.
  VehicleId: string;
  CustUserId: string;
  Merged: boolean;
  MergedFromVehicleId?: string;
  MovedInvoiceVehicles: number;
  MovedWorkItems: number;
  MovedImages: number;
}

export interface PaginatedResponseDto<T> {
  pageNum: number;
  pageSize: number;
  totalPages: number;
  data: T;
  errors?: string[];
}

export interface VehicleQueryParams {
  pageNumber?: number;
  pageSize?: number;
  sort?: 'make-model-year' | 'firstName' | 'lastName' | 'email' | 'vin';
  sortDir?: 'asc' | 'desc';
  // Only return vehicles owned by this customer user.
  custUserId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VehicleService {
  private apiUrl = `${environment.apiUrl}/vehicles`;
  private imageApiUrl = `${environment.apiUrl}/vehicle-images`;

  constructor(private http: HttpClient) {}

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = error.error?.message || `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    
    console.error('Vehicle Service Error:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // ============= VEHICLE CRUD OPERATIONS =============

  /**
   * Create a new vehicle
   */
  createVehicle(vehicleData: CreateVehicleDto): Observable<VehicleResponseDto> {
    return this.http.post<VehicleResponseDto>(
      this.apiUrl,
      vehicleData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all vehicles (filtered by user permissions)
   */
  getAllVehicles(): Observable<VehicleResponseDto[]> {
    return this.http.get<VehicleResponseDto[]>(
      this.apiUrl
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all vehicles with images (filtered by user permissions, paginated)
   */
  getAllVehiclesWithImages(params?: VehicleQueryParams): Observable<PaginatedResponseDto<VehicleWithImagesResponseDto[]>> {
    const queryParams: any = {};

    if (params?.pageNumber) queryParams.pageNumber = params.pageNumber.toString();
    if (params?.pageSize) queryParams.pageSize = params.pageSize.toString();
    if (params?.sort) queryParams.sort = params.sort;
    if (params?.sortDir) queryParams.sortDir = params.sortDir;
    if (params?.custUserId) queryParams.custUserId = params.custUserId;

    return this.http.get<PaginatedResponseDto<VehicleWithImagesResponseDto[]>>(
      `${this.apiUrl}/with-images`,
      { params: queryParams }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get vehicles by customer user ID
   */
  getVehiclesByUserId(userId: string): Observable<VehicleResponseDto[]> {
    return this.http.get<VehicleResponseDto[]>(
      `${this.apiUrl}/user/${userId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get a single vehicle by ID
   */
  getVehicleById(vehicleId: string): Observable<VehicleResponseDto> {
    return this.http.get<VehicleResponseDto>(
      `${this.apiUrl}/${vehicleId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get a vehicle with all its images
   */
  getVehicleWithImages(vehicleId: string): Observable<VehicleWithImagesResponseDto> {
    return this.http.get<VehicleWithImagesResponseDto>(
      `${this.apiUrl}/${vehicleId}/with-images`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Update a vehicle
   */
  updateVehicle(vehicleId: string, updateData: UpdateVehicleDto): Observable<VehicleResponseDto> {
    return this.http.patch<VehicleResponseDto>(
      `${this.apiUrl}/${vehicleId}`,
      updateData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Delete a vehicle
   */
  deleteVehicle(vehicleId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${vehicleId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  // ============= OWNERSHIP TRANSFER =============

  /**
   * Vehicles already in the destination customer's garage that look like the
   * same physical car, strongest identifier first. Requires EDIT_ALL_GARAGES.
   */
  getTransferCandidates(vehicleId: string, custUserId: string): Observable<VehicleMergeCandidateDto[]> {
    return this.http.get<VehicleMergeCandidateDto[]>(
      `${this.apiUrl}/${vehicleId}/transfer-candidates`,
      { params: { custUserId } }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Move a vehicle to another customer, optionally merging it into one they
   * already have. Past invoices stay billed to their original customer.
   */
  transferVehicle(vehicleId: string, transferData: TransferVehicleDto): Observable<TransferVehicleResponseDto> {
    return this.http.post<TransferVehicleResponseDto>(
      `${this.apiUrl}/${vehicleId}/transfer`,
      transferData
    ).pipe(
      catchError(this.handleError)
    );
  }

  // ============= VEHICLE IMAGE OPERATIONS =============

  /**
   * Upload a single image for a vehicle
   */
  uploadVehicleImage(vehicleId: string, imageFile: File, isPrimary: boolean = false): Observable<VehicleImageResponseDto> {
    const formData = new FormData();
    formData.append('image', imageFile, imageFile.name);
    formData.append('isPrimary', isPrimary.toString());

    return this.http.post<VehicleImageResponseDto>(
      `${this.imageApiUrl}/upload/${vehicleId}`,
      formData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Upload multiple images for a vehicle
   */
  uploadMultipleVehicleImages(vehicleId: string, imageFiles: File[]): Observable<VehicleImageResponseDto[]> {
    const formData = new FormData();
    
    // Append each file to the form data
    imageFiles.forEach((file) => {
      formData.append('images', file, file.name);
    });

    return this.http.post<VehicleImageResponseDto[]>(
      `${this.imageApiUrl}/upload-multiple/${vehicleId}`,
      formData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Replace an existing vehicle image
   */
  replaceVehicleImage(imageId: string, newImageFile: File): Observable<VehicleImageResponseDto> {
    const formData = new FormData();
    formData.append('image', newImageFile, newImageFile.name);

    return this.http.put<VehicleImageResponseDto>(
      `${this.imageApiUrl}/${imageId}/replace`,
      formData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all images for a vehicle
   */
  getVehicleImages(vehicleId: string): Observable<VehicleImageResponseDto[]> {
    return this.http.get<VehicleImageResponseDto[]>(
      `${this.imageApiUrl}/vehicle/${vehicleId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get a single vehicle image by ID
   */
  getVehicleImageById(imageId: string): Observable<VehicleImageResponseDto> {
    return this.http.get<VehicleImageResponseDto>(
      `${this.imageApiUrl}/${imageId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get just the signed URL for an image
   */
  getImageUrl(imageId: string): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(
      `${this.imageApiUrl}/${imageId}/url`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Update a vehicle image metadata (e.g., set as primary)
   */
  updateVehicleImage(imageId: string, updateData: UpdateVehicleImageDto): Observable<VehicleImageResponseDto> {
    return this.http.patch<VehicleImageResponseDto>(
      `${this.imageApiUrl}/${imageId}`,
      updateData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Delete a vehicle image
   */
  deleteVehicleImage(imageId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.imageApiUrl}/${imageId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Delete all images for a vehicle
   */
  deleteAllVehicleImages(vehicleId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.imageApiUrl}/vehicle/${vehicleId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  // ============= UTILITY METHODS =============

  /**
   * Gray "No Image" placeholder (SVG data URI) for vehicles without photos.
   */
  getDefaultImage(): string {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2U1ZTdlYiIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD4KPC9zdmc+';
  }

  /**
   * Validate image file before upload
   */
  validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: 'Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.' 
      };
    }

    if (file.size > maxSize) {
      return { 
        valid: false, 
        error: 'File size exceeds 10MB limit.' 
      };
    }

    return { valid: true };
  }

  /**
   * Create a preview URL for an image file (for displaying before upload)
   */
  createImagePreview(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * Revoke a preview URL to free memory
   */
  revokeImagePreview(url: string): void {
    URL.revokeObjectURL(url);
  }
}